import { useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import { useLoading } from '../../../hooks/useLoading';
import { useSubscription } from '../../../hooks/useSubscription';
import { uploadCandidate, uploadJob, analyzeCandidateJob } from '../../../services';
import { ApiError } from '../../../lib/errors';
import type { Analysis } from '../../../types/models';
import { supabase } from '../../../lib/supabase';

export interface AnalysisInput {
  candidateName: string;
  cvText: string;
  jobTitle: string;
  jobDescription: string;
  organizationId?: string | null;
}

export function useAnalysis() {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isLoading, withLoading } = useLoading();
  const { addToast } = useToast();
  const { refetch: refetchSubscription, organization } = useSubscription();

  const analyze = async (input: AnalysisInput) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new ApiError('Not authenticated', 401);
      }
      const userId = session.user.id;
      const orgId = input.organizationId || organization?.organizationId;

      console.log('Auth check:', { userId, isAuthenticated: true, organizationId: orgId });

      // Step 1: Upload candidate - remove organization_id property as it's not in the type
      const candidateData = {
        name: input.candidateName,
        cv_text: input.cvText,
        created_at: new Date().toISOString(),
        user_id: userId
      };

      // Pass organization data through console only - we'll add proper typing later
      console.log('Using organization context:', orgId);
      const candidate = await uploadCandidate(candidateData);
      console.log('Candidate uploaded:', candidate);

      // Step 2: Upload job - update signature to match actual function
      const job = await uploadJob(
        input.jobTitle,
        input.jobDescription,
        '' // company (optional parameter)
      );
      console.log('Job uploaded:', job);

      // Step 3: Analyze - match function signature
      console.log('Starting analysis with:', {
        candidateId: candidate.id,
        jobId: job.id,
        organizationId: orgId
      });

      const result = await analyzeCandidateJob({
        candidateId: candidate.id,
        jobId: job.id
      });

      console.log('Analysis completed:', result);
      setAnalysis(result);
      setError(null);
      addToast('Analysis completed successfully', 'success');
      
      refetchSubscription();
      
      return result;

    } catch (err: any) {
      console.error('Analysis error:', {
        error: err,
        type: err instanceof ApiError ? 'ApiError' : typeof err,
        message: err.message,
        status: err.status,
        details: err.details
      });

      const errorMessage = err instanceof ApiError 
        ? err.message 
        : 'An unexpected error occurred. Please try again.';

      setError(errorMessage);
      addToast(errorMessage, 'error');
      throw err;
    }
  };

  // Function to reset analysis state
  const resetAnalysis = () => {
    setAnalysis(null);
    setError(null);
    // isLoading should reset automatically via useLoading hook when analysis starts
  };

  return {
    analysis,
    error,
    isLoading,
    analyze: (input: AnalysisInput) => withLoading(analyze(input)),
    resetAnalysis // Expose the reset function
  };
}